import { publicCazaAttribution, publicItemDescription } from './publicMetadata';
import {
  assignRouteSlugs,
  composeDisambiguatedSlug,
  routeIdOf,
  slugifyRouteName,
} from './routeSlugs.js';

export { publicItemDescription } from './publicMetadata';

export const SITE_URL = 'https://zghartacaza.com';
export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'ar'];

export const TAB_ROUTES = {
  map: '',
  explore: 'explore',
  events: 'events',
  guide: 'guide',
  favorites: 'saved',
};

const ROUTE_TABS = Object.entries(TAB_ROUTES).reduce((acc, [tab, segment]) => {
  acc[segment] = tab;
  return acc;
}, {});

export function stripTrailingSlash(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function normalizeLocale(value) {
  return SUPPORTED_LOCALES.includes(value) ? value : DEFAULT_LOCALE;
}

export const slugify = slugifyRouteName;

function baseItemSlug(item) {
  return slugify(item?.name || item?.nameAr || routeIdOf(item));
}

function itemIdSlugs(item) {
  return [...new Set([routeIdOf(item), item?.id].map(slugify))];
}

// The generator disambiguates a colliding name by locality for every member of the
// group (#354), so `/en/places/st-georges-church-ardeh` is now a real crawler-facing
// URL. The app must resolve those URLs to the same row, and must push the same shape
// into history — otherwise a visitor arriving from a search result lands on a page
// that cannot find its own item, and the canonical the app writes disagrees with the
// sitemap. `items` is the catalog list the slug is assigned within; without it, the
// caller gets the plain name slug, exactly as before.
const assignedSlugCache = new WeakMap();

function assignedSlugsFor(items) {
  const cached = assignedSlugCache.get(items);
  if (cached) return cached;
  const { slugs } = assignRouteSlugs(items);
  assignedSlugCache.set(items, slugs);
  return slugs;
}

export function itemSlug(item, items = null) {
  if (!Array.isArray(items) || !items.length) return baseItemSlug(item);
  const index = items.findIndex(candidate => candidate === item || (
    candidate?.id != null && item?.id != null && String(candidate.id) === String(item.id)
  ));
  if (index === -1) return baseItemSlug(item);
  return assignedSlugsFor(items)[index] || baseItemSlug(item);
}

function disambiguatedSlugCandidates(item) {
  const base = baseItemSlug(item);
  const locality = String(item?.village || item?.location || '').trim();
  const ids = itemIdSlugs(item);
  const candidates = [];
  const withLocality = locality ? composeDisambiguatedSlug(base, slugify(locality)) : '';
  if (withLocality) {
    candidates.push(withLocality);
    ids.forEach(id => candidates.push(composeDisambiguatedSlug(withLocality, id)));
  }
  ids.forEach(id => candidates.push(composeDisambiguatedSlug(base, id)));
  return candidates;
}

export function findItemBySlug(items, slug) {
  const normalizedSlug = slugify(slug);
  const list = items || [];

  // 1. The slug the generator actually published for this collection. Checking this
  //    first is what makes the app agree with the sitemap even when a disambiguated
  //    slug happens to equal another row's plain name — that row is itself part of a
  //    colliding group in that case, so it was disambiguated too and does not own the
  //    URL any more.
  const assignedIndex = list.length ? assignedSlugsFor(list).indexOf(normalizedSlug) : -1;
  if (assignedIndex !== -1) return list[assignedIndex];

  // 2. Plain name and id slugs, so a URL that was live before disambiguation still
  //    resolves to the row it resolved to then.
  const direct = list.find(item => (
    baseItemSlug(item) === normalizedSlug ||
    itemIdSlugs(item).includes(normalizedSlug)
  ));
  if (direct) return direct;

  // 3. Locality-qualified forms, as a safety net for a list that differs slightly from
  //    the one the generator assigned over.
  return list.find(item => disambiguatedSlugCandidates(item).includes(normalizedSlug)) || null;
}

export function getLocalizedPath({ lang = DEFAULT_LOCALE, tab = 'map', itemType = null, item = null, slug = null } = {}) {
  const locale = normalizeLocale(lang);
  const base = locale === DEFAULT_LOCALE ? '/en' : `/${locale}`;

  if (itemType && (item || slug)) {
    const detailSegment = {
      place: 'places',
      business: 'businesses',
      event: 'events',
    }[itemType];
    if (detailSegment) return `${base}/${detailSegment}/${slug || itemSlug(item)}`;
  }

  const routeSegment = TAB_ROUTES[tab] || '';
  return routeSegment ? `${base}/${routeSegment}` : base;
}

export function getCanonicalPath(route) {
  return stripTrailingSlash(getLocalizedPath(route));
}

export function getCanonicalUrl(route) {
  return `${SITE_URL}${getCanonicalPath(route)}`;
}

export function getAlternateLinks(route) {
  const alternates = SUPPORTED_LOCALES.map(lang => ({
    lang,
    href: `${SITE_URL}${getCanonicalPath({ ...route, lang })}`,
  }));

  return [
    ...alternates,
    {
      lang: 'x-default',
      href: `${SITE_URL}${getCanonicalPath({ ...route, lang: DEFAULT_LOCALE })}`,
    },
  ];
}

export function parseSeoPath(pathname) {
  const cleanPath = stripTrailingSlash(pathname || '/');
  const segments = cleanPath.split('/').filter(Boolean).map(segment => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  });
  const first = segments[0];
  const hasLocale = SUPPORTED_LOCALES.includes(first);
  const lang = hasLocale ? first : DEFAULT_LOCALE;
  const routeSegments = hasLocale ? segments.slice(1) : segments;
  const [segment = '', slug] = routeSegments;

  if (segment === 'places') {
    return { lang, tab: 'explore', itemType: slug ? 'place' : null, slug: slug || '' };
  }
  if (segment === 'businesses') {
    return { lang, tab: 'explore', itemType: slug ? 'business' : null, slug: slug || '' };
  }
  if (segment === 'events' && slug) {
    return { lang, tab: 'events', itemType: 'event', slug };
  }
  return {
    lang,
    tab: ROUTE_TABS[segment] || 'map',
    itemType: null,
    slug: '',
  };
}

export function routeForTab(tab, lang = DEFAULT_LOCALE) {
  return {
    lang: normalizeLocale(lang),
    tab,
    itemType: null,
    slug: '',
  };
}

export function routeForItem(itemType, item, lang = DEFAULT_LOCALE, items = null) {
  return {
    lang: normalizeLocale(lang),
    tab: itemType === 'event' ? 'events' : 'explore',
    itemType,
    item,
    slug: itemSlug(item, items),
  };
}

export function routeTitle(route, item) {
  const lang = normalizeLocale(route?.lang);
  const isAr = lang === 'ar';
  if (item) {
    const name = isAr ? (item.nameAr || item.name) : item.name;
    const place = publicCazaAttribution(item, lang).locality;
    return `${name} | ${place} | Zgharta Caza Tourism`;
  }

  const titles = {
    map: {
      en: 'Zgharta Caza Tourism Guide | North Lebanon',
      ar: 'دليل السياحة في قضاء زغرتا | شمال لبنان',
    },
    explore: {
      en: 'Zgharta Caza Places Directory | Restaurants, Shops & Stays',
      ar: 'دليل أماكن قضاء زغرتا | مطاعم ومتاجر وإقامات',
    },
    events: {
      en: 'Zgharta Caza Events & Village Calendar',
      ar: 'فعاليات قضاء زغرتا ورزنامة القرى',
    },
    guide: {
      en: 'Zgharta Caza Travel Guide',
      ar: 'دليل السفر إلى قضاء زغرتا',
    },
    favorites: {
      en: 'Saved Zgharta Caza Trip Stops',
      ar: 'محطات رحلة زغرتا المحفوظة',
    },
  };

  return titles[route?.tab]?.[lang] || titles.map[lang];
}

export function routeDescription(route, item) {
  const lang = normalizeLocale(route?.lang);
  if (item) {
    return publicItemDescription(item, lang).slice(0, 155);
  }

  const descriptions = {
    map: {
      en: 'Discover villages, restaurants, heritage sites, churches, trails, stays, and events across Zgharta Caza in North Lebanon.',
      ar: 'اكتشف قرى ومطاعم ومعالم وكنائس ودروب وإقامات وفعاليات قضاء زغرتا في شمال لبنان.',
    },
    explore: {
      en: 'Browse curated places, restaurants, cafes, shops, hotels, nature, heritage, and religious destinations in Zgharta Caza.',
      ar: 'تصفح أماكن ومطاعم ومقاهي ومتاجر وفنادق وطبيعة وتراث ووجهات دينية مختارة في قضاء زغرتا.',
    },
    events: {
      en: 'Find festivals, cultural nights, religious gatherings, and nature events across villages in Zgharta Caza.',
      ar: 'اعثر على المهرجانات والأمسيات الثقافية والتجمعات الدينية وفعاليات الطبيعة في قرى قضاء زغرتا.',
    },
    guide: {
      en: 'Plan a mountain trip through Zgharta Caza, from Ehden and Horsh Ehden to Qozhaya, Bnachii, Miziara, and Zgharta.',
      ar: 'خطط رحلة جبلية في قضاء زغرتا من إهدن وحرج إهدن إلى قزحيا وبنشعي ومزيارة وزغرتا.',
    },
    favorites: {
      en: 'Save and organize restaurants, churches, viewpoints, stays, and village discoveries for a Zgharta Caza trip.',
      ar: 'احفظ ونظم المطاعم والكنائس والإطلالات والإقامات واكتشافات القرى لرحلة في قضاء زغرتا.',
    },
  };

  return descriptions[route?.tab]?.[lang] || descriptions.map[lang];
}
